"use client";

import { useEffect } from "react";

/**
 * Route-level error boundary. Catches any uncaught error thrown while
 * rendering a page or its nested segments below the root layout. Without
 * this, an unhandled throw is a blank white screen for the user.
 *
 * `global-error.tsx` is the separate last-resort boundary for errors in the
 * root layout itself.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Surfaced to the console for now; the Sentry capture is wired in a
    // follow-up PR so production errors land somewhere visible.
    console.error("[error-boundary]", error);
  }, [error]);

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 px-6 text-center">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-[var(--on-surface)]">
          Something went wrong
        </h1>
        <p className="max-w-md text-sm text-[var(--on-surface-variant)]">
          This part of the workspace hit an unexpected error. Your data is
          safe — you can retry, or head back to the dashboard.
        </p>
        {error.digest && (
          <p className="mt-1 text-xs text-[var(--on-surface-muted)]">
            Reference: {error.digest}
          </p>
        )}
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80"
          style={{ background: "var(--primary)" }}
        >
          Try again
        </button>
        <a
          href="/"
          className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--on-surface)] transition-colors hover:bg-[var(--surface-high)]"
          style={{ border: "1px solid var(--outline)" }}
        >
          Go to dashboard
        </a>
      </div>
    </div>
  );
}
